import { RevxButton } from 'components/RevxButton';
import { RevxCircleLabel } from 'components/RevxCircleLabel';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useCanvaMatchView } from 'hooks';
import './CanvaMatchView.less';

/** Arrow leaving a box — the usual "this takes you somewhere else" mark on a text link. */
const ExternalLinkGlyph = () => (
  <svg
    className="canva-match-view_glyph"
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M9.5 2h4.5v4.5"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M13.5 2.5 8 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path
      d="M12.5 9.75V13a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1h3.25"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * The landing a Canva buyer arrives on: the products that can hold the parked design, gathered
 * from across the whole catalogue, with a way out to browse everything.
 *
 * Shared by both gallery pages on purpose. The Canva app decides where to send the buyer, and it
 * ships separately from this app — until every installed copy is on the new version, buyers keep
 * arriving on the old destination, so both have to be able to host this.
 *
 * No category strip: the point of the view is that the products which fit are spread across
 * categories, so a category picker would put back the wall it exists to remove.
 */
export const CanvaMatchView = (props: {
  children: ReactNode;
  /** How many products fit the design; shown on the heading. Undefined until the list settles. */
  count?: number;
  /**
   * Heading name. Defaults to the match view's own heading (gallery.canvaMatch.matchViewTitle);
   * the category picker passes the category's own name, so the buyer sees which one they opened
   * rather than a second copy of the landing view.
   */
  title?: string;
}) => {
  const { t } = useTranslation();
  const { browseAllProducts } = useCanvaMatchView();

  return (
    <div className="canva-match-view">
      {/* The same circle-and-name heading the shop uses for a category, so the matches read as
          one more group of products rather than as a special screen. */}
      <RevxFlexLayout justify="justify-center" align="center" className="canva-match-view_header">
        <RevxCircleLabel
          text={props.count ?? 0}
          name={props.title || t('gallery.canvaMatch.matchViewTitle')}
        />
      </RevxFlexLayout>

      {props.children}

      {/* Below the matches, not beside the heading: this is what the buyer reaches for once the
          recommendations have not covered it, so it belongs at the end of them. */}
      <RevxFlexLayout justify="justify-center" align="center" className="canva-match-view_footer">
        <RevxButton
          type="link"
          className="canva-match-view_link"
          onClick={browseAllProducts}
        >
          {t('gallery.canvaMatch.browseAllProducts')}
          <ExternalLinkGlyph />
        </RevxButton>
      </RevxFlexLayout>
    </div>
  );
};
